import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts';
import { CleviaLogo } from '@/components/CleviaLogo';
import { Shield, ArrowRight, Wallet, BarChart3, Users, Activity } from 'lucide-react';

export default function AdminChoicePage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const firstName = user?.name?.split(' ')[0] || 'Admin';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-emerald-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-emerald-950/20 flex flex-col items-center justify-center p-4">
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMxNmM0N2YiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PHBhdGggZD0iTTM2IDE4YzEuNjU3IDAgMyAxLjM0MyAzIDNzLTEuMzQzIDMtMyAzLTMtMS4zNDMtMy0zIDEuMzQzLTMgMy0zek0zNiAzNmMxLjY1NyAwIDMgMS4zNDMgMyAzcy0xLjM0MyAzLTMgMy0zLTEuMzQzLTMtMyAxLjM0My0zIDMtM3pNMTggMThjMS42NTcgMCAzIDEuMzQzIDMgM3MtMS4zNDMgMy0zIDMtMy0xLjM0My0zLTMgMS4zNDMtMyAzLTN6TTE4IDM2YzEuNjU3IDAgMyAxLjM0MyAzIDNzLTEuMzQzIDMtMyAzLTMtMS4zNDMtMy0zIDEuMzQzLTMgMy0zeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50 dark:opacity-20" />

      <div className="relative z-10 w-full max-w-3xl">
        {/* Header */}
        <div className="text-center mb-10">
          <CleviaLogo size="lg" className="justify-center mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Bienvenue, {firstName} 👋
          </h1>
          <p className="text-gray-500 dark:text-gray-400">Où souhaitez-vous aller ?</p>
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* clevia App Card */}
          <button
            onClick={() => navigate('/dashboard')}
            className="group relative bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700/50 p-8 text-left hover:border-[#16c47f]/40 hover:shadow-xl hover:shadow-[#16c47f]/5 transition-all duration-300 cursor-pointer overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#16c47f]/5 to-transparent rounded-bl-full" />
            <div className="relative">
              <div className="w-14 h-14 bg-gradient-to-br from-[#16c47f] to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-[#16c47f]/20 mb-6 group-hover:scale-110 transition-transform duration-300">
                <Wallet className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-[#16c47f] transition-colors">
                clevia App
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                Gérez vos finances personnelles, suivez vos comptes, transactions et objectifs.
              </p>
              <div className="flex items-center text-[#16c47f] text-sm font-medium">
                <span>Ouvrir l'app</span>
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </button>

          {/* clevia Admin Card */}
          <button
            onClick={() => navigate('/admin')}
            className="group relative bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700/50 p-8 text-left hover:border-violet-400/40 hover:shadow-xl hover:shadow-violet-500/5 transition-all duration-300 cursor-pointer overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-violet-500/5 to-transparent rounded-bl-full" />
            <div className="relative">
              <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-violet-500/20 mb-6 group-hover:scale-110 transition-transform duration-300">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-violet-500 transition-colors">
                clevia Admin
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                Dashboard administrateur — KPIs, analytics, utilisateurs et performances.
              </p>
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <Users className="w-3.5 h-3.5" />
                  <span>Users</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <BarChart3 className="w-3.5 h-3.5" />
                  <span>KPIs</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <Activity className="w-3.5 h-3.5" />
                  <span>Analytics</span>
                </div>
              </div>
              <div className="flex items-center text-violet-500 text-sm font-medium">
                <span>Ouvrir le dashboard</span>
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
